<template>
  <div :id="selectId" ref="reference" class="typeahead typeahead--searchable position-relative">
    <div
      :id="`${selectId}_typeahead__combobox`"
      ref="typeAheadContainer"
      :aria-expanded="isDropDownOpen"
      role="combobox"
      :aria-owns="`${selectId}_typeahead__listbox`"
      :aria-controls="`${selectId}_typeahead__listbox`"
      :class="{ 'typeahead--open': isDropDownOpen }"
      aria-label="search for options"
      class="typeahead__dropdown-toggle position-relative"
    >
      <div class="typeahead__selected-options" role="presentation" @click="handleOptionsClick">
        <span
          v-if="!hasInput"
          ref="selectedPlaceholderRef"
          class="typeahead__selected"
          :style="ghostOpacityDynamicStyles"
          :class="{ 'is-placeholder': isPlaceholder }"
          >{{ model || placeholder }}</span
        >
        <input
          ref="typeaheadQuery"
          v-model="model"
          type="search"
          aria-autocomplete="list"
          :aria-labelledby="`${selectId}_typeahead__combobox`"
          :aria-controls="`${selectId}_typeahead__listbox`"
          autocomplete="off"
          :aria-activedescendant="`${selectId}_option_${hoverIndex}`"
          class="typeahead__search"
          @input="handleOnInput"
          @blur="handleBlurred"
        />
      </div>
      <div class="typeahead__actions">
        <button
          v-if="isDisplayClearButton"
          title="clear selected"
          tabindex="0"
          aria-label="clear selected"
          class="typeahead__action me-space-xs text-dark-2 btn btn-link"
          @click="clearSelected"
        >
          <font-awesome-icon icon="fa-close" size="lg" />
        </button>
      </div>
    </div>

    <Transition name="fade" @before-leave="handleCollapseHide" @enter="handleCollapseShown">
      <ul
        v-show="isDropDownOpen"
        :id="`${selectId}_typeahead__listbox`"
        ref="dropdownList"
        role="listbox"
        :style="{ ...dynamicStyles, ...floatingStyles }"
        tabindex="-1"
        class="typeahead__dropdown-menu"
        :class="{ 'typeahead__dropdown-menu--shown': isDropDownOpen }"
      >
        <template v-if="results.length > 0">
          <li
            v-for="({ item, refIndex }, i) in results"
            :id="`${selectId}_option_${i}`"
            :key="refIndex"
            :ref="(el) => functionRef(el, i)"
            role="option"
            :value="item.value"
            :aria-selected="hoverIndex === i"
            class="typeahead__dropdown-option"
            :class="{
              'typeahead__dropdown-option--highlighted': hoverIndex === i
            }"
            @mouseenter="handleHover(i)"
            @click="handleChangeOption(item)"
            v-html="highlightText(item.text)"
          ></li>
        </template>
        <template v-else>
          <li class="typeahead__dropdown-option--empty">No results found.</li>
        </template>
      </ul>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { VariantOption } from "@/types/types";
import { autoUpdate, offset, useFloating } from "@floating-ui/vue";
import { onClickOutside, onKeyStroke, refDebounced, useCssVar, watchDebounced } from "@vueuse/core";
import { useFuse, type UseFuseOptions } from "@vueuse/integrations/useFuse";
import type { FuseSortFunctionArg } from "fuse.js";

export type TypeAheadSelectSortFunc = (a: FuseSortFunctionArg, b: FuseSortFunctionArg) => number;

const model = defineModel<string | null>({ required: true });
interface Props {
  id: string;
  options: TypeAheadSearchListOption[];
  height?: string;
  hoverColor?: VariantOption;
  highlightTextColor?: VariantOption;
  highlightBgColor?: VariantOption;
  hoverTextColor?: VariantOption;
  preselectOpacity?: string;
  placeholder?: string;
  alphaSort?: boolean;
  sortFn?: TypeAheadSelectSortFunc;
}

export type TypeAheadSearchListOption = {
  value: string;
  text: string;
};

/*
 ********************************************
 * props
 ********************************************
 */
const props = withDefaults(defineProps<Props>(), {
  height: "350px",
  highlightBgColor: "secondary",
  highlightTextColor: "dark-3",
  placeholder: "Search...",
  hoverTextColor: "light-1",
  hoverColor: "secondary",
  preselectOpacity: "0.6" // minimum for color contrast requirement
});

/*
 ********************************************
 * data
 ********************************************
 */
const isDropDownOpen = ref(false);
const isHoverActive = ref(false);
const hoverIndex = ref(0);
const isFocused = ref(false);
const resultElements = ref<Array<Element | globalThis.ComponentPublicInstance | null> | []>([]);
const dropdownList = ref<HTMLUListElement | null>(null);
const reference = ref<HTMLInputElement | null>(null);
const typeaheadQuery = ref<HTMLInputElement | null>(null);

/**
 * Emits for TypeAheadSelect component.
 *
 * @emits hidden - Emitted when the dropdown is hidden.
 * @emits shown - Emitted when the dropdown is shown.
 * @emits changed - Emitted when the toggle is clicked.
 * @emits searching - Emitted when searching is performed.
 * @emits cleared - Emitted when the input is cleared.
 * @emits debounceChanged - Emitted when the modal value is changed. Useful for analytics events.
 */
const emits = defineEmits<{
  (e: "hidden" | "shown" | "cleared"): void;
  (e: "changed", val: TypeAheadSearchListOption): void;
  (e: "debounceChanged", text: string | null): void;
}>();
/*
 ********************************************
 * computed
 ********************************************
 */
// prioritize custom sort function then alpha, then use default
const sortingMethod = computed(() => {
  if (props.sortFn) {
    return props.sortFn;
  } else if (props.alphaSort == true) {
    return alphaFuseSort;
  } else {
    return defaultFuseSort;
  }
});

const ghostOpacityDynamicStyles = computed(() => {
  return {
    "--typeahead-preselect-opacity": props.preselectOpacity
  };
});
const isDropDownOpenClone = computed(
  () => isFocused.value && model.value && model.value.length > 0
);
const isPlaceholder = computed(() => {
  return model.value === null && isDropDownOpen.value === false;
});
const selectId = computed(() => props.id);
const functionRef = (el: Element | globalThis.ComponentPublicInstance | null, index: number) => {
  resultElements.value[index] = el;
};
const isDisplayClearButton = computed(() => {
  return model.value !== null && (model.value as string).length > 0;
});

const hasInput = computed(() => model.value !== null && model.value?.length > 0);
const dynamicStyles = computed(() => {
  return {
    "--dropdown-height": props.height,
    "--highlight-bg-color": hoverColor.value,
    "--highlight-text-color": hoverTextColor.value
  };
});
const highlightText = (text: string) => {
  const searchTerm = String(model.value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(searchTerm, "gi");
  return text.replace(regex, (match) => `<span class="fw-bold">${match}</span>`);
};
/*
 ********************************************
 * methods
 ********************************************
 */
// fuse's default sort
const defaultFuseSort = (a: FuseSortFunctionArg, b: FuseSortFunctionArg) => {
  return a.score === b.score ? (a.idx < b.idx ? -1 : 1) : a.score < b.score ? -1 : 1;
};

// alpha sort for fuse
const alphaFuseSort = (a: FuseSortFunctionArg, b: FuseSortFunctionArg) => {
  if (a.matches && b.matches && a.matches[0].value && b.matches[0].value) {
    if (a.matches[0].value < b.matches[0].value) {
      return -1;
    }
    if (a.matches[0].value > b.matches[0].value) {
      return 1;
    }
    return 0;
  } else return 0;
};
const handleOnInput = async () => {
  await nextTick();
  // Android keyboard fix
  model.value = typeaheadQuery.value?.value || null;
  isFocused.value = true;
};
const handleBlurred = async () => {
  await nextTick();
  isFocused.value = false;
};
const handleOptionsClick = () => {
  typeaheadQuery.value?.focus();
};
const handleChangeOption = (option: TypeAheadSearchListOption) => {
  handleDropDownClose();
  model.value = option.text;
  emits("changed", option);
};
const clearSelected = () => {
  handleDropDownClose();
  emits("cleared");
  model.value = null;
};
const handleHover = (i: number) => {
  hoverIndex.value = i;
  isHoverActive.value = true;
};
const handleDropDownClose = () => {
  isDropDownOpen.value = false;
  typeaheadQuery.value?.blur();
};
onKeyStroke("ArrowDown", (e) => {
  if (isDropDownOpen.value) {
    e.preventDefault();
    if (hoverIndex.value < results.value.length - 1) {
      hoverIndex.value++;
    }
  }
});
// arrow up
onKeyStroke("ArrowUp", (e: Event) => {
  if (isDropDownOpen.value) {
    if (hoverIndex.value > 0) {
      e.preventDefault();
      hoverIndex.value--;
    }
  }
});
onKeyStroke(["Tab", "Escape"], () => {
  if (isDropDownOpen.value) {
    handleDropDownClose();
  }
});
onKeyStroke("Enter", (e: Event) => {
  if (isDropDownOpen.value) {
    e.preventDefault();
    if (results.value[hoverIndex.value] && results.value[hoverIndex.value].item) {
      handleChangeOption(results.value[hoverIndex.value].item);
      model.value = results.value[hoverIndex.value].item.text;
    }
    typeaheadQuery.value?.blur();
  }
});
const handleCollapseHide = (el: Element) => {
  (el as HTMLElement).style.pointerEvents = "none";
};
const handleCollapseShown = (el: Element) => {
  (el as HTMLElement).style.pointerEvents = "auto";
};
const debounced = refDebounced(isDropDownOpenClone, 350);

watch(debounced, (val) => {
  if (val === true) {
    isDropDownOpen.value = true;
    hoverIndex.value = 0;
  } else {
    isDropDownOpen.value = false;
  }
});

watchDebounced(
  model,
  (newValue) => {
    emits("debounceChanged", newValue);
  },
  { debounce: 500 }
);

/*
 ********************************************
 * Watchers
 ********************************************
 */

watch(isDropDownOpen, (val) => {
  if (val === true) {
    emits("shown");
  } else {
    emits("hidden");
  }
});

/*
 ********************************************
 * Hooks
 ********************************************
 */
const hoverColor = useCssVar(`--rds-${props.highlightBgColor}`, dropdownList);
const hoverTextColor = useCssVar(`--rds-${props.highlightTextColor}`, dropdownList);
onClickOutside(reference, () => {
  handleDropDownClose();
});

const { floatingStyles } = useFloating(reference, dropdownList, {
  whileElementsMounted: autoUpdate,
  strategy: "absolute",
  open: isDropDownOpen,
  transform: false,
  placement: "bottom",
  middleware: [offset(-1)]
});
const fuseOptions: UseFuseOptions<TypeAheadSearchListOption> = {
  fuseOptions: {
    keys: ["text"],
    isCaseSensitive: false,
    includeScore: false,
    useExtendedSearch: false,
    includeMatches: true,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: true,
    location: 0,
    ignoreFieldNorm: true,
    threshold: 0.3,
    distance: 200,
    sortFn: sortingMethod.value
  },
  matchAllWhenSearchEmpty: true
};

const searchOptions = ref<TypeAheadSearchListOption[]>([]);
const { results } = useFuse(model.value || "", searchOptions, fuseOptions);

watchEffect(() => {
  searchOptions.value = props.options;
});
</script>
<style lang="scss" scoped>
.typeahead--open {
  .typeahead__selected {
    position: absolute;
    opacity: var(--typeahead-preselect-opacity);
  }
}
.typeahead {
  position: relative;
  .is-placeholder {
    opacity: var(--typeahead-preselect-opacity);
  }
  &--disabled {
    opacity: var(--typeahead-preselect-opacity);
    pointer-events: none;
    cursor: not-allowed;
  }
  &__dropdown-toggle {
    display: flex;
    background-color: var(--rds-body-bg);
    border: 1px solid var(--rds-border-color);
    border-radius: 0;
    white-space: normal;
    padding: 8px;
  }
  &__selected-options {
    display: flex;
    flex-basis: 100%;
    flex-grow: 1;
    flex-wrap: nowrap;
    padding: 0 2px;
    position: relative;
    overflow: hidden;
  }
  &__selected {
    display: flex;
    align-self: center;
    font-size: var(--rds-font-size);
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 0;
  }
  &__actions {
    display: flex;
    align-items: center;
  }
  &__search {
    flex-grow: 1;
    padding: 0;
    margin: 0;
    position: relative;
    z-index: 5;
    background-color: transparent;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--rds-text-color);
    appearance: none;
    border: 1px solid transparent;
    box-shadow: none;
    z-index: 1;
    width: 0;
    max-width: 100%;
    outline: none;
  }
  &__action {
    width: 24px;
    height: 24px;
    appearance: none;
    padding: 0;
    border: 0;
    background-color: transparent;
    cursor: pointer;
  }
  &__dropdown {
    &-menu {
      width: 100%;
      z-index: 1000;
      padding: 5px 0;
      margin: 0.125rem 0 0;
      font-size: 1rem;
      color: var(--rds-text-color);
      text-align: left;
      list-style: none;
      background-color: var(--rds-body-bg);
      background-clip: padding-box;
      border: var(--rds-border-width) solid var(--rds-border-color);
      border-top: 0;
      border-radius:
        0 0 var(--rds-border-radius-sm),
        var(--rds-border-radius-sm);
      max-height: var(--dropdown-height);
      margin: 0;
      min-width: 100%;
      vertical-align: top;
      overflow-y: scroll;
      box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);
    }
    &-option {
      padding: 8px 12px;
      cursor: pointer;
      background-color: var(--rds-body-bg);
      color: var(--rds-text-color);
      &--highlighted {
        background-color: var(--highlight-bg-color);
        color: var(--highlight-text-color);
      }
      &--empty {
        padding: 8px 12px;
        color: var(--rds-text-color);
      }
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
